// src/api.js   (or src/apis.js if that’s your convention)
import axios from "axios";
import * as Constants from "./constants/constants"; // adjust path if needed
import { isTokenExpired, redirectToLoginExpired } from "./utils/tokenExpiry";

// Create a single axios instance using your Constants.API_URL
const api = axios.create({
  baseURL: Constants.API_URL,        // e.g. "https://support.srjedu.com"
  timeout: 20000,
});

// Login/OTP endpoints are public — a 401 from these means "wrong credentials"
// or "invalid/expired OTP", not "your session expired". They must never
// trigger the auto-logout/redirect behavior below.
const PUBLIC_AUTH_PATHS = ["/login", "/login/send-otp", "/login/verify-otp"];

const isPublicAuthRequest = (config) => {
  const url = String(config?.url || "");
  return PUBLIC_AUTH_PATHS.some((p) => url === p || url.startsWith(`${p}?`));
};

// Guards against ever sending "Bearer undefined" / "Bearer null" /
// "Bearer [object Object]" if a bad value ever ends up in localStorage.
const getValidToken = () => {
  const token = localStorage.getItem("token");
  if (!token || token === "undefined" || token === "null" || token === "[object Object]") {
    return null;
  }
  return token;
};

// Attach token automatically (optional but common in your project)
api.interceptors.request.use((config) => {
  const token = getValidToken();
  // An already-expired token is never sent: end the session up front
  // rather than firing a request that can only come back 401.
  if (token && !isPublicAuthRequest(config) && isTokenExpired(token)) {
    redirectToLoginExpired();
    return Promise.reject(new (axios.CanceledError || Error)("Session expired"));
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (config.headers && "Authorization" in config.headers) {
    delete config.headers.Authorization;
  }
  return config;
});

// Centralized 401 handling: only treat it as "session invalid" when the
// failing request actually carried our auth token — never for public
// login/OTP requests, so a wrong-password/invalid-OTP error keeps showing
// on the login page instead of bouncing the user away from it.
let isHandlingSessionExpiry = false;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const config = error?.config || {};
    const hadAuthHeader = !!config?.headers?.Authorization;

    if (
      status === 401 &&
      hadAuthHeader &&
      !isPublicAuthRequest(config) &&
      !isHandlingSessionExpiry
    ) {
      isHandlingSessionExpiry = true;

      redirectToLoginExpired();

      // Release the guard shortly after so a future session/login isn't permanently blocked.
      setTimeout(() => {
        isHandlingSessionExpiry = false;
      }, 2000);
    }

    // Always reject with the original error so calling pages keep reading
    // err?.response?.data?.message / err?.response?.status exactly as before.
    return Promise.reject(error);
  }
);

export default api;

/* -------------------- EXPENSES (Reimbursement) -------------------- */

// finance
export const financeQueue   = (params = {}) => api.get('/accounts/finance-queue', { params });
export const financeApprove = (id, payload) => api.put(`/accounts/finance-approve/${id}`, payload);
export const financeReject  = (id, payload) => api.put(`/accounts/finance-reject/${id}`, payload); // <— NEW
export const markPaid = (id, payload) =>
  api.post(`/expenses/${id}/pay`, payload);



// list & details
export const getMyApprovalQueue = (userId) =>
  api.get(`/approvals/queue`, { params: { user_id: userId } });

export const mgrApprove = (id, payload) =>
  api.post(`/expenses/${id}/manager/approve`, payload);
export const mgrRequestChanges = (id, payload) =>
  api.post(`/expenses/${id}/manager/request-changes`, payload);
export const mgrReject = (id, payload) =>
  api.post(`/expenses/${id}/manager/reject`, payload);
export const finApprove = (id, payload) =>
  api.post(`/expenses/${id}/finance/approve`, payload);


export const listReports    = (params)     => api.get("/expenses", { params });
export const getReport      = (id)         => api.get(`/expenses/${id}`);

// validate a receipt image before saving
export const checkReceipt = (id, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api
    .post(`/expenses/${id}/receipts/check`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then(r => r.data);
};

export const createReport = (payload) =>
  api.post('/expenses', payload).then(r => r.data);  // r.data = { id: <newId> }

export const approveAny = (id, payload) =>
  api.post(`/approvals/${id}/approve`, payload).then(r => r.data);

// items
export const addItem        = (id, payload)=> api.post(`/expenses/${id}/items`, payload);
export const delItem        = (id, itemId) => api.delete(`/expenses/${id}/items/${itemId}`);

// submit to manager
export const submitReport   = (id, payload)=> api.post(`/expenses/${id}/submit`, payload);

// receipts (metadata; replace with real uploader if needed)
export const addReceipt     = (id, payload)=> api.post(`/expenses/${id}/receipts`, payload);

/* -------------------- MANAGER -------------------- */

export const managerQueue   = (params)     => api.get("/manager/expenses", { params });
export const managerApprove = (id, payload)=> api.post(`/expenses/${id}/manager/approve`, payload);
export const managerReqChg  = (id, payload)=> api.post(`/expenses/${id}/manager/request-changes`, payload);
export const managerReject  = (id, payload)=> api.post(`/expenses/${id}/manager/reject`, payload);

/* -------------------- FINANCE -------------------- */


